import { z } from "zod/v4"
import { ItemIdSchema, LiveIdSchema, SpaceBackgroundIdSchema, SpaceBGMIdSchema, SpaceKey, SpaceKeySchema, UserIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { SelectionDenormalized } from "./selection"
import { validateUserFreeInputText } from "../user-free-input-text-constraint"
import { MediaImage } from "../common"

export const SpaceBackgroundKindShared = z.literal("shared").describe("共通背景。")
export const SpaceBackgroundKindInventory = z.literal("inventory").describe("インベントリアイテム。")

export const SpaceBackgroundKind = z.union([
  SpaceBackgroundKindShared,
  SpaceBackgroundKindInventory,
])

export const SpaceBGMKindShared = z.literal("shared").describe("共通BGM。")
export const SpaceBGMKindInventory = z.literal("inventory").describe("インベントリのアイテム。")

export const SpaceBGMKind = z.union([
  SpaceBGMKindShared,
  SpaceBGMKindInventory,
])

export const SpaceBGMShared = z.object({
  id: SpaceBGMIdSchema.nullable(),
  kind: SpaceBGMKindShared,
})

export const SpaceBGMInventory = z.object({
  id: ItemIdSchema,
  kind: SpaceBGMKindInventory,
})

export const SpaceBGMValue = z.discriminatedUnion("kind", [
  SpaceBGMShared,
  SpaceBGMInventory,
])

export const SpaceBGM = z.intersection(SpaceBGMValue, z.object({
  volume: z.int().min(0).max(100).describe("BGMのボリューム。"),
  updated_at: z.number().optional().describe("BGM を変更した日時(UNIX Timestamp mills)。値がない場合はBGMを変更したことがない。"),
})).describe("BGM設定。")

export type SpaceBGM = z.infer<typeof SpaceBGM>

export const SpaceBackgroundShared = z.object({
  id: SpaceBackgroundIdSchema,
  kind: SpaceBackgroundKindShared,
})

export const SpaceBackgroundInventory = z.object({
  id: ItemIdSchema,
  kind: SpaceBackgroundKindInventory,
})

export const SpaceBackgroundValue = z.discriminatedUnion("kind", [
  SpaceBackgroundShared,
  SpaceBackgroundInventory,
])

export const SpaceBackground = z.intersection(SpaceBackgroundValue, z.object({
  updated_at: z.number().optional().describe("背景を変更した日時(UNIX Timestamp mills)。値がない場合は背景を変更したことがない。"),
})).describe("背景設定。")

export type SpaceBackground = z.infer<typeof SpaceBackground>

export const SpaceThumbnailLook = z.object({
  url: z.string(),
  image: MediaImage.optional(),
  event_category: z.string().optional(),
  created_at: z.number(),
})

export type SpaceThumbnailLook = z.infer<typeof SpaceThumbnailLook>

export const SpaceDocumentMeta = z.object({
  name: z.string().describe("スペースの名前。"),
  user_id: UserIdSchema.describe("スペースのオーナーのユーザーID。"),
  bgm: SpaceBGM,
  background: SpaceBackground,
  thumbnails: z.object({
    looks: SpaceThumbnailLook.array(),
  }).describe("スペースサムネイルに使用する画像セット。"),

  // 以下からスペース自動削除用のスペース不使用検出用のパラメータ
  space_activity_status: z.enum(["active", "notified_auto_deletion", "notification_failure", "deletion_failure"]).describe("スペース自動削除用ステータス。通話していない状態で放置されると、削除予定が通知されてnotified_auto_deletionになり、更に放置するとスペースが削除される。通話開始するとactiveに戻る。バックエンド参照用で、フロントで使うことは意図していない"),
  auto_deletion_sequence_failure_count: z.int().min(0).optional().describe("スペース自動削除の警告通知の送信失敗/自動削除処理が失敗した回数。0以上の整数。バックエンド参照用で、フロントで使うことは意図していない"),
  auto_delete_scheduled_at: z.number().nullable().optional().describe("スペース自動削除の予定時刻。通話に使用されているスペースであればnull。自動削除警告通知の3日後。この時刻を過ぎたnotified_auto_deletionステータスのスペースが自動削除対象となる。"),
  // 以下はホーム画面用の表示パラメータ
  // See: https://www.notion.so/popopo-inc/4352797b807840fca7f61a46ac3172b5
  selections: z.array(SelectionDenormalized).optional().describe("進行中の投稿募集の情報。"),
  non_mute_online_user_count: z.int().min(0).describe("ミュート状態ではない通話中ユーザーの数。"),
  online_user_count: z.int().min(0).optional().describe("通話中ユーザーの数（オンライン状態のSpaceUserの数）。"),
  // TODO: dev/stageリリースからしばらくしたらoptionalを外す
  thumbnails_next_update_scheduled_at: z.number().nullable().optional().describe("次回のスペースサムネイル更新予定時刻。nullの場合は更新しない。バックエンド参照用で、フロントで使うことは意図していない"),
  latest_started_at: z.number().nullable().optional().describe("スペースがアクティブになった（1人目がオンラインになった）最新の時刻。"),
  last_activity_at: z.number().optional().describe("スペース内で最後にアクティビティ（メッセージ送信等）があった日時。スペース自動削除判定に使用する。"),
})

export type SpaceDocumentMeta = z.infer<typeof SpaceDocumentMeta>

export const SpaceDocumentBody = SpaceDocumentMeta.extend({
  live_id: LiveIdSchema.nullable(),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type SpaceDocumentBody = z.infer<typeof SpaceDocumentBody>

export type SpaceDocument = SpaceDocumentBody & {
  id: SpaceKey,
}

/**
 * 非正規化されたスペース情報。
 * スペース削除後もデータを参照可能にするために、他のドキュメントに埋め込んで使用する。
 */
export const SpaceDenormalized = z.object({
  key: SpaceKeySchema.describe("スペースキー。"),
  name: z.string().describe("スペース名。"),
  user_id: UserIdSchema.describe("スペースオーナーのユーザーID。"),
  bgm: SpaceBGM.describe("BGM設定。"),
  background: SpaceBackground.describe("背景設定。"),
  thumbnails: z.object({
    looks: SpaceThumbnailLook.array(),
  }).describe("スペースサムネイルに使用する画像セット。"),
  non_mute_online_user_count: z.int().min(0).describe("ミュート状態ではない通話中ユーザーの数。"),
  online_user_count: z.int().min(0).optional().describe("通話中ユーザーの数。"),
})

export type SpaceDenormalized = z.infer<typeof SpaceDenormalized>

export class SpaceDocuments {

  static toDocumentData(space: SpaceDocument): object {
    return {
      ...space,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SpaceDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SpaceKey(doc.id),
      name: data["name"],
      live_id: data["live_id"],
      user_id: data["user_id"],
      background: data["background"] ? {
        id: data["background"]["id"],
        kind: data["background"]["kind"],
        updated_at: data["background"]["updated_at"] ?? 0,
      } : {
        id: "",
        kind: "",
        updated_at: 0,
      },
      bgm: data["bgm"] ? {
        id: data["bgm"]["id"],
        kind: data["bgm"]["kind"],
        volume: data["bgm"]["volume"],
        updated_at: data["bgm"]["updated_at"],
      } : {
        id: null,
        kind: "shared",
        volume: 100,
        updated_at: 0,
      },
      selections: data["selections"],
      non_mute_online_user_count: data["non_mute_online_user_count"],
      online_user_count: data["online_user_count"],
      thumbnails: data["thumbnails"] ? {
        looks: data["thumbnails"]["looks"] ?? [],
      } : { looks: [] },
      thumbnails_next_update_scheduled_at: data["thumbnails_next_update_scheduled_at"],
      latest_started_at: data["latest_started_at"],
      last_activity_at: data["last_activity_at"],
      space_activity_status: data["space_activity_status"],
      auto_deletion_sequence_failure_count: data["auto_deletion_sequence_failure_count"],
      auto_delete_scheduled_at: data["auto_delete_scheduled_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export class Spaces {

  static toSpaceDenormalized(space: SpaceDocument): SpaceDenormalized {
    return {
      key: space.id,
      name: space.name,
      user_id: space.user_id,
      bgm: space.bgm,
      background: space.background,
      thumbnails: space.thumbnails,
      non_mute_online_user_count: space.non_mute_online_user_count,
      online_user_count: space.online_user_count,
    }
  }

  public static readonly NAME_MIN_LENGTH = 1
  public static readonly NAME_MAX_LENGTH = 64

  public static readonly LIMIT_OWN = 1000
  public static readonly LIMIT_USERS = 100
  // スペースの参加上限を20人から30人にしたい - https://www.notion.so/popopo-inc/20-30-1155e03d57e280cfba4ec0fa6de6be32
  public static readonly LIMIT_USERS_ONLINE = 30
  // 1ユーザーが参加できるスペース数の上限 - https://popopo-inc.slack.com/archives/C07BA42T4FJ/p1768456189279729?thread_ts=1768455546.865199&cid=C07BA42T4FJ
  public static readonly LIMIT_JOINED_SPACES = 1000

  static asNoBGM(volume: number | undefined, updated_at: number | undefined): SpaceBGM {
    return {
      id: null,
      kind: "shared",
      volume: volume ?? 100,
      updated_at,
    }
  }

  static isSomeSelectionPublished(space: SpaceDocument): boolean {
    if (space.selections === undefined || space.selections.length === 0) {
      return false
    }

    return space.selections.some(s => s.status === "published")
  }

}

export const CreateSpaceRequest = z.object({
  name: validateUserFreeInputText({minLength: Spaces.NAME_MIN_LENGTH, maxLength: Spaces.NAME_MAX_LENGTH}),
  backgroundId: z.string(),
  bgmId: z.string().optional(),
  background: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("shared"),
      id: SpaceBackgroundIdSchema,
    }),
    z.object({
      kind: z.literal("inventory"),
      id: ItemIdSchema,
    }),
  ]).optional(),
  bgm: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("shared"),
      id: SpaceBGMIdSchema.or(z.literal("")),
    }),
    z.object({
      kind: z.literal("inventory"),
      id: ItemIdSchema,
    }),
  ]).optional(),
})

export type CreateSpaceRequest = z.infer<typeof CreateSpaceRequest>

export const UpdateSpaceRequest = z.object({
  name: validateUserFreeInputText({ minLength: Spaces.NAME_MIN_LENGTH, maxLength: Spaces.NAME_MAX_LENGTH }),
})

export type UpdateSpaceRequest = z.infer<typeof UpdateSpaceRequest>


export const UpdateSpaceBGMRequest = z.object({
  bgm: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("shared"),
      id: SpaceBGMIdSchema.or(z.literal("")),
      volume: z.int().min(0).max(100)
    }),
    z.object({
      kind: z.literal("inventory"),
      id: ItemIdSchema,
      volume: z.int().min(0).max(100)
    }),
  ])
})

export type UpdateSpaceBGMRequest = z.infer<typeof UpdateSpaceBGMRequest>

export const UpdateSpaceBackgroundRequest = z.object({
  background: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("shared"),
      id: SpaceBackgroundIdSchema,
    }),
    z.object({
      kind: z.literal("inventory"),
      id: ItemIdSchema,
    }),
  ]),
})

export type UpdateSpaceBackgroundRequest = z.infer<typeof UpdateSpaceBackgroundRequest>

/**
 * 管理者によるスペース作成リクエスト
 * - 共有リソース・インベントリどちらも指定可能
 */
export const CreateSpaceByAdminRequest = z.object({
  userId: UserIdSchema.describe("スペースのオーナーとなるユーザーのID"),
  name: validateUserFreeInputText({ minLength: Spaces.NAME_MIN_LENGTH, maxLength: Spaces.NAME_MAX_LENGTH }),
  background: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("shared"),
      id: SpaceBackgroundIdSchema,
    }),
    z.object({
      kind: z.literal("inventory"),
      id: ItemIdSchema,
    }),
  ]).describe("背景の設定"),
  bgm: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("shared"),
      id: SpaceBGMIdSchema,
    }),
    z.object({
      kind: z.literal("inventory"),
      id: ItemIdSchema,
    }),
  ]).optional().describe("BGMの設定（省略時はBGMなし）"),
})

export type CreateSpaceByAdminRequest = z.infer<typeof CreateSpaceByAdminRequest>
